import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { ArrowLeft, Download } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import { packagingPurchaseOrderV2Api } from 'api/packagingPurchaseOrderV2Api';
import { usePackagingPOBackHref } from 'components/packaging/components/packagingPOV2/usePackagingPOBackHref';
import { Alert } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader } from 'shadcn/components/ui/Card';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadPendingChanges,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from 'shared/components/bulkUpload';

export const PACKAGING_COLUMN_DEFS = [
    { key: 'facility', label: 'Facility', required: true, conditionallyRequired: false },
    { key: 'scheduledDeliveryDate', label: 'Scheduled Delivery Date', required: true, conditionallyRequired: false },
    { key: 'referenceNumber', label: 'Reference Number', required: false, conditionallyRequired: false },
    { key: 'shipToCrossDockName', label: 'Ship To Cross Dock', required: false, conditionallyRequired: false },
    { key: 'vendorName', label: 'Vendor', required: true, conditionallyRequired: false },
    { key: 'packagingID', label: 'Packaging ID', required: true, conditionallyRequired: false },
    { key: 'vendorSku', label: 'Vendor Sku', required: false, conditionallyRequired: false },
    { key: 'quantityCases', label: 'Quantity Cases', required: true, conditionallyRequired: false },
    { key: 'unitPrice', label: 'Unit Price', required: true, conditionallyRequired: false },
    { key: 'palletsPerSku', label: 'Pallets per SKU', required: true, conditionallyRequired: false },
    { key: 'unitsPerCase', label: 'Units per Case', required: true, conditionallyRequired: false },
    { key: 'notes', label: 'Notes', required: false, conditionallyRequired: false },
    { key: 'poNumber', label: 'PO Number', required: false, conditionallyRequired: false },
    { key: 'action', label: 'Action', required: true, conditionallyRequired: false },
];

export function BulkCreatePackagingPOV2() {
    const history = useHistory();
    const backHref = usePackagingPOBackHref();
    const api = useMemo(
        () => ({
            edit: (key, params, rowsToUpdate, rowsToDelete) =>
                packagingPurchaseOrderV2Api.bulkEdit(key, params, rowsToUpdate, rowsToDelete),
            fetch: (key, params, signal) => packagingPurchaseOrderV2Api.bulkFetch(key, params, signal),
            persist: (key, params) => packagingPurchaseOrderV2Api.bulkPersist(key, params),
            upload: (file, params) => packagingPurchaseOrderV2Api.bulkUpload(file, params),
        }),
        [],
    );
    const bulkUpload = useBulkUpload({
        api,
        params: {},
    });
    const [persistSuccess, setPersistSuccess] = useState(false);
    const hasData = bulkUpload.rows.length > 0;
    const errorCount = bulkUpload.validationErrors.length;

    const handleFileSelect = useCallback(
        (file) => {
            if (file) {
                bulkUpload.uploadFile(file);
            }
        },
        [bulkUpload],
    );

    const handlePersist = useCallback(async () => {
        try {
            const result = await bulkUpload.persist();
            const hasRowErrors = result?.persistResults?.errors?.length > 0;
            if (!hasRowErrors) {
                setPersistSuccess(true);
                bulkUpload.reset();
            }
        } catch {
            // Error is already captured in bulkUpload.persistError
        }
    }, [bulkUpload]);

    const handleStartNew = useCallback(() => {
        setPersistSuccess(false);
    }, []);

    const handleDownloadTemplate = useCallback(() => {
        const headers = PACKAGING_COLUMN_DEFS.map((column) => {
            if (column.required) {
                return `${column.label}*`;
            }
            if (column.conditionallyRequired) {
                return `${column.label}(*)`;
            }
            return column.label;
        }).join(',');

        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 7);
        const exampleRow = {
            facility: 'BWI1',
            scheduledDeliveryDate: dayjs(nextWeek).format('MM/DD/YY'),
            referenceNumber: 'REF-123',
            shipToCrossDockName: '',
            vendorName: 'Acme Produce',
            packagingID: '789',
            vendorSku: 'VSKU-001',
            quantityCases: '100',
            unitPrice: '25.00',
            palletsPerSku: '40',
            unitsPerCase: '12',
            notes: 'This is an example',
            poNumber: '',
            action: 'ADD',
        };

        const values = PACKAGING_COLUMN_DEFS.map((column) => {
            const value = exampleRow[column.key] ?? '';
            if (String(value).includes(',')) {
                return `"${value}"`;
            }
            return value;
        }).join(',');

        const csvContent = `${headers}\n${values}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'packaging_purchase_order_bulk_upload_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    return (
        <div className="flex w-full flex-col gap-6">
            <div className="flex flex-wrap items-start gap-3 lg:items-center">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => history.push(backHref)}
                    className="mr-auto"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                    Back to POs
                </Button>
                <div className="flex flex-1 flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                        Bulk Create Packaging Purchase Orders
                    </h1>
                </div>
            </div>
            <Card>
                <CardHeader>
                    <CardDescription>
                        Upload a CSV file to create multiple packaging purchase orders at once. The file will be
                        validated before you can save.
                        <br />
                        There is currently a limit of 100 rows per bulk upload.
                        <br />
                        <b>The following actions are supported</b>
                        <br />
                        <b>ADD</b> - Add a new packaging purchase order row
                        <br />
                        <b>REMOVE</b> - Remove an existing packaging purchase order row
                        <br />
                        <b>UPDATE</b> - Update an existing packaging purchase order row
                        <br />
                        <br />
                        <b>
                            Multiple lines can be grouped as one PO by keeping the following fields the same:
                            <br />
                            Facility, Scheduled Delivery Date, Reference Number, and Vendor
                        </b>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {!hasData && !persistSuccess && (
                        <>
                            <div className="flex justify-end">
                                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Download Template
                                </Button>
                            </div>
                            <BulkUploadDropzone
                                onFileSelect={handleFileSelect}
                                disabled={[BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                                    bulkUpload.status,
                                )}
                                isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                            />
                        </>
                    )}

                    <BulkUploadStatus
                        entityName="Packaging Purchase Order"
                        fetchError={bulkUpload.fetchError}
                        handleStartNew={handleStartNew}
                        hasData={hasData}
                        persistError={bulkUpload.persistError}
                        persistErrors={bulkUpload.persistErrors}
                        persistSuccess={persistSuccess}
                        status={bulkUpload.status}
                        uploadError={bulkUpload.uploadError}
                    />

                    {hasData &&
                        !persistSuccess &&
                        !bulkUpload.hasPersistErrors &&
                        ![BULK_UPLOAD_STATUS.SUBMITTING, BULK_UPLOAD_STATUS.PERSISTING].includes(bulkUpload.status) && (
                            <>
                                <div className="flex items-center justify-between">
                                    <div>
                                        {errorCount > 0 ? (
                                            <Alert variant="destructive" className="inline-flex">
                                                {errorCount} validation error{errorCount !== 1 ? 's' : ''} found. Please
                                                fix the errors before saving. You can hover over the highlighted cells
                                                for more information.
                                            </Alert>
                                        ) : (
                                            <Alert className="inline-flex border-green-500 text-green-700">
                                                No validation errors. Ready to save.
                                            </Alert>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        {bulkUpload.hasUnsavedChanges && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={bulkUpload.clearPendingChanges}
                                                >
                                                    Discard Changes
                                                </Button>
                                                <Button variant="default" size="sm" onClick={bulkUpload.submitChanges}>
                                                    Submit Changes
                                                </Button>
                                            </>
                                        )}
                                        <Button variant="outline" size="sm" onClick={bulkUpload.reset}>
                                            Start Over
                                        </Button>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={handlePersist}
                                            disabled={!bulkUpload.canPersist}
                                        >
                                            Save Purchase Orders
                                        </Button>
                                    </div>
                                </div>

                                <BulkUploadTable
                                    columnDefs={PACKAGING_COLUMN_DEFS}
                                    rows={bulkUpload.rows}
                                    errorsByCell={bulkUpload.errorsByCell}
                                    pendingEdits={bulkUpload.pendingEdits}
                                    updateCell={bulkUpload.updateCell}
                                    pendingDeletes={bulkUpload.pendingDeletes}
                                    toggleRowDelete={bulkUpload.toggleRowDelete}
                                />

                                <BulkUploadPendingChanges
                                    pendingDeletes={bulkUpload.pendingDeletes}
                                    pendingEdits={bulkUpload.pendingEdits}
                                    rowsLength={bulkUpload.rows.length}
                                />
                            </>
                        )}
                </CardContent>
            </Card>
        </div>
    );
}

BulkCreatePackagingPOV2.displayName = 'BulkCreatePackagingPOV2';
